import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Container,
  Header,
  Message,
  Modal,
  Form,
} from "semantic-ui-react";

import * as MessageList from "components/MessageList";
import * as AccountService from "services/accounts";
import * as AccountsTable from "components/Admin/Accounts/AccountsTable";
import * as AccountsTableColumns from "components/Admin/Accounts/AccountsTableColumns";
import { getApiGatewayClient } from "services/api";

const RegisteredAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [messages, sendMessage] = MessageList.useMessages();
  const [deleteText, setDeleteText] = useState("");
  const [deleteTextError, setDeleteTextError] = useState(false);

  const refreshAccounts = () =>
    AccountService.fetchRegisteredAccounts().then((accounts) =>
      setAccounts(accounts)
    );

  // Initial load
  useEffect(() => {
    refreshAccounts().finally(() => setLoading(false));
  }, []);

  const onSelectAccount = useCallback(
    (account) => setSelectedAccount(account),
    [setSelectedAccount]
  );

  const getAppDetail = async () => {
    return getApiGatewayClient("/admin/developer", {
      operation: "readApp",
      tableName: "DevPortalClientApp",
      payload: {
        Key: {
          UserName: selectedAccount.EmailAddress,
        },
      },
    }).then((res) => {
      if (res.success) {
        if (res.Item && res.Item.AppId) {
          return res.Item;
        } else {
          return {};
        }
      }
      return null;
    });
  };

  const onConfirmDelete = useCallback(async () => {
    if (deleteText !== selectedAccount.EmailAddress) {
      setDeleteTextError(true);
      return;
    }
    setLoading(true);
    setDeleteModalOpen(false);
    setDeleteText("");
    try {
      await AccountService.deleteAccountByUserId(selectedAccount.UserId);
      sendMessage((dismiss) => (
        <DeleteSuccessMessage account={selectedAccount} dismiss={dismiss} />
      ));
      await refreshAccounts().catch((err) => Promise.resolve(err));
    } catch (error) {
      sendMessage((dismiss) => (
        <DeleteFailureMessage
          account={selectedAccount}
          dismiss={dismiss}
          errorMessage={error.message}
        />
      ));
    } finally {
      setLoading(false);
    }
  }, [sendMessage, selectedAccount, deleteText]);

  const onCheckAppExist = async () => {
    const appDetailRes = await getAppDetail();
    if (appDetailRes && appDetailRes.AppId) {
      sendMessage((dismiss) => (
        <DeleteFailureAppExistMessage
          account={selectedAccount}
          dismiss={dismiss}
        />
      ));
      return;
    } else {
      setDeleteModalOpen(true);
    }
  };

  const onConfirmPromote = useCallback(async () => {
    setLoading(true);
    setPromoteModalOpen(false);
    try {
      await AccountService.promoteAccountByUserId(selectedAccount.UserId);
      sendMessage((dismiss) => (
        <PromoteSuccessMessage account={selectedAccount} dismiss={dismiss} />
      ));
    } catch (error) {
      sendMessage((dismiss) => (
        <PromoteFailureMessage
          account={selectedAccount}
          dismiss={dismiss}
          errorMessage={error.message}
        />
      ));
    } finally {
      setLoading(false);
    }
  }, [sendMessage, selectedAccount]);

  return (
    <Container fluid style={{ padding: "2em" }}>
      <Header as="h1">Registered accounts</Header>
      <MessageList.MessageList messages={messages} />
      <AccountsTable.AccountsTable
        accounts={accounts}
        columns={[
          AccountsTableColumns.EmailAddress,
          AccountsTableColumns.DateRegistered,
          AccountsTableColumns.RegistrationMethod,
          AccountsTableColumns.ApiKeyId,
        ]}
        loading={loading}
        selectedAccount={selectedAccount}
        onSelectAccount={onSelectAccount}
      >
        <TableActions
          onCheckAppExist={onCheckAppExist}
          canDelete={!loading && selectedAccount}
          canPromote={!loading && selectedAccount}
          onClickPromote={() => setPromoteModalOpen(true)}
        />
      </AccountsTable.AccountsTable>
      <DeleteAccountModal
        deleteTextError={deleteTextError}
        deleteText={deleteText}
        onChangeDeleteText={(value) => {
          setDeleteText(value);
          setDeleteTextError(false);
        }}
        account={selectedAccount}
        onConfirm={onConfirmDelete}
        open={deleteModalOpen}
        onClose={() => {
          setDeleteText("");
          setDeleteTextError(false);
          setDeleteModalOpen(false);
        }}
      />
      <PromoteAccountModal
        account={selectedAccount}
        onConfirm={onConfirmPromote}
        open={promoteModalOpen}
        onClose={() => setPromoteModalOpen(false)}
      />
    </Container>
  );
};
export default RegisteredAccounts;

const TableActions = React.memo(
  ({ canDelete, onCheckAppExist, canPromote, onClickPromote }) => (
    <Button.Group>
      <Button
        content="Delete"
        disabled={!canDelete}
        onClick={onCheckAppExist}
      />
      <Button
        content="Promote to Admin"
        disabled={!canPromote}
        onClick={onClickPromote}
      />
    </Button.Group>
  )
);

const DeleteAccountModal = React.memo(
  ({
    account,
    deleteText,
    deleteTextError,
    onConfirm,
    open,
    onClose,
    onChangeDeleteText,
  }) =>
    account && (
      <Modal size="small" open={open} onClose={onClose}>
        <Modal.Header>Confirm deletion</Modal.Header>
        <Modal.Content>
          <p>
            Are you sure you want to delete the account{" "}
            <strong>{account.EmailAddress}</strong>, and deactivate the
            associated API key? This action is irreversible.
          </p>
          <p>
            Enter the <strong>{account.EmailAddress}</strong> to continue.
          </p>
          <Form>
            <Form.Input
              fluid
              error={deleteTextError}
              value={deleteText}
              onChange={(e, { value }) => {
                onChangeDeleteText(value);
              }}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Cancel</Button>
          <Button negative onClick={onConfirm}>
            Delete
          </Button>
        </Modal.Actions>
      </Modal>
    )
);

const PromoteAccountModal = React.memo(
  ({ account, onConfirm, open, onClose }) =>
    account && (
      <Modal size="small" open={open} onClose={onClose}>
        <Modal.Header>Confirm promotion</Modal.Header>
        <Modal.Content>
          <p>
            Are you sure you want to promote the account{" "}
            <strong>{account.EmailAddress}</strong> to Admin? This will allow
            the account to perform any Admin actions, including deleting and
            promoting other accounts.
          </p>
          <p>
            Only the owner of the Developer Portal can demote the account,
            through the Cognito console.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Cancel</Button>
          <Button negative onClick={onConfirm}>
            Promote
          </Button>
        </Modal.Actions>
      </Modal>
    )
);

const DeleteSuccessMessage = React.memo(({ account, dismiss }) => (
  <Message onDismiss={dismiss} positive>
    <Message.Content>
      Deleted account <strong>{account.EmailAddress}</strong>.
    </Message.Content>
  </Message>
));

const DeleteFailureMessage = React.memo(
  ({ account, errorMessage, dismiss }) => (
    <Message onDismiss={dismiss} negative>
      <Message.Content>
        <p>
          Failed to delete account <strong>{account.EmailAddress}</strong>.
        </p>
        {errorMessage && <p>Error message: {errorMessage}</p>}
      </Message.Content>
    </Message>
  )
);
const DeleteFailureAppExistMessage = React.memo(({ account, dismiss }) => (
  <Message onDismiss={dismiss} negative>
    <Message.Content>
      <p>
        Failed to delete account <strong>{account.EmailAddress}</strong> as one
        app associated with it.
      </p>
      <p>Please remove the app for this account and retry.</p>
    </Message.Content>
  </Message>
));

const PromoteSuccessMessage = React.memo(({ account, dismiss }) => (
  <Message onDismiss={dismiss} positive>
    <Message.Content>
      Promoted account <strong>{account.EmailAddress}</strong>.
    </Message.Content>
  </Message>
));

const PromoteFailureMessage = React.memo(
  ({ account, errorMessage, dismiss }) => (
    <Message onDismiss={dismiss} negative>
      <Message.Content>
        <p>
          Failed to promote account <strong>{account.EmailAddress}</strong>.
        </p>
        {errorMessage && <p>Error message: {errorMessage}</p>}
      </Message.Content>
    </Message>
  )
);
